// src/ReviewsPage.jsx
//
// Página pública de avaliações e confiança — equivalente a consultamais.org/reviews.
// Acessível em /reviews sem login.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';
import { logUxEvent } from './telemetry/uxLogger';
import { openDocExternalInApp } from './utils/openDocExternalInApp';

const REVIEWS_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'AggregateRating',
  '@id': 'https://consultamais.app/reviews#aggregate',
  itemReviewed: {
    '@type': 'MedicalBusiness',
    '@id': 'https://consultamais.app/#org',
    name: 'Consulta+',
    url: 'https://consultamais.app/',
  },
  ratingValue: '4.8',
  bestRating: '5',
  worstRating: '1',
  ratingCount: 10000,
  reviewAspect: 'Teleconsulta médica por chat',
};

const APPLE_STORE_URL = 'https://apps.apple.com/br/app/consulta-mais/id6748291915';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.consultamais.app4&hl=pt_BR';
const RECLAME_AQUI_URL =
  'https://www.reclameaqui.com.br/empresa/esmanhotto-servicos-medicos-ltda/sobre/';
const CFM_BUSCA_URL = 'https://portal.cfm.org.br/busca-medicos';
const EXEMPLO_ATESTADO_URL = `${process.env.PUBLIC_URL || ''}/exemplo-atestado.pdf`;
const EXEMPLO_RECEITA_URL = `${process.env.PUBLIC_URL || ''}/exemplo-receita.pdf`;

const CARD_CSS = `
  .reviews-page__cards {
    display: grid;
    gap: 0.85rem;
    margin: 0 0 1.25rem;
  }

  .reviews-page__card {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.95rem 1rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.85rem;
    background: #fafafa;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  }

  .reviews-page__card:hover,
  .reviews-page__card:focus-visible {
    border-color: rgba(211, 47, 47, 0.35);
    box-shadow: 0 4px 14px rgba(211, 47, 47, 0.08);
    background: #fff;
    outline: none;
  }

  .reviews-page__card-icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.65rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
  }

  .reviews-page__card-body {
    flex: 1;
    min-width: 0;
  }

  .reviews-page__card-title {
    margin: 0 0 0.15rem;
    font-size: 0.98rem;
    font-weight: 700;
    color: #1f2937;
  }

  .reviews-page__card-sub {
    margin: 0;
    font-size: 0.82rem;
    color: #6b7280;
    line-height: 1.4;
  }

  .reviews-page__card--static {
    cursor: default;
  }

  .reviews-page__card--static:hover,
  .reviews-page__card--static:focus-visible {
    border-color: rgba(0, 0, 0, 0.08);
    box-shadow: none;
    background: #fafafa;
  }

  .reviews-page__card-arrow {
    flex-shrink: 0;
    color: #d32f2f;
    font-weight: 700;
    font-size: 1.1rem;
  }

  .reviews-page__highlight {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: #fff8e1;
    color: #92400e;
    font-size: 0.82rem;
    font-weight: 600;
    border-radius: 999px;
    padding: 0.25rem 0.65rem;
    margin-bottom: 0.75rem;
    border: 1px solid rgba(146, 64, 14, 0.15);
  }

  .reviews-page__doctor {
    margin: 0 0 0.75rem;
  }

  .reviews-page__doctor p {
    margin: 0 0 0.5rem;
  }

  .reviews-page__doctor p:last-child {
    margin-bottom: 0;
  }

  /* Capturas do Reclame Aqui (reputação ÓTIMO + selo verificado) */
  .reviews-page__ra-reputation {
    display: flex;
    justify-content: center;
    margin: 0.75rem 0 0;
  }
  .reviews-page__ra-reputation img {
    width: min(340px, 92vw);
    height: auto;
    border-radius: 12px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  }
  .reviews-page__ra-seal {
    display: flex;
    justify-content: center;
    margin: 1rem 0 0;
  }
  .reviews-page__ra-seal,
  .reviews-page__ra-seal * {
    pointer-events: none;
    cursor: default;
  }
  .reviews-page__ra-verify-link {
    display: block;
    text-align: center;
    margin: 0.65rem 0 1.25rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: #D32F2F;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .reviews-page__ra-verify-link:hover,
  .reviews-page__ra-verify-link:focus-visible {
    color: #B71C1C;
  }
  .reviews-page__ra-verify-link:focus-visible {
    outline: 2px solid #D32F2F;
    outline-offset: 3px;
    border-radius: 4px;
  }

  /* CTA primário — mesmo visual do botão da home */
  .reviews-page__cta-wrap {
    display: flex;
    justify-content: center;
    margin: 1.75rem 0 1rem;
  }
  .reviews-page__cta {
    background: #D32F2F;
    color: #fff;
    border: none;
    border-radius: 999px;
    font-family: inherit;
    font-size: 1.05rem;
    font-weight: 600;
    padding: 1rem 2rem;
    min-width: min(280px, 100%);
    min-height: 48px;
    cursor: pointer;
    box-shadow: 0 12px 28px rgba(211, 47, 47, 0.18);
    transition: background 0.18s ease, transform 0.18s ease;
    -webkit-tap-highlight-color: transparent;
  }
  .reviews-page__cta:hover { background: #B71C1C; }
  .reviews-page__cta:active { transform: scale(0.98); }
  .reviews-page__cta:focus-visible {
    outline: 2px solid #B71C1C;
    outline-offset: 3px;
  }
`;

function ReviewCard({ href, icon, title, subtitle, external = true, staticCard = false }) {
  const body = (
    <>
      <span className="reviews-page__card-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="reviews-page__card-body">
        <p className="reviews-page__card-title">{title}</p>
        {subtitle ? <p className="reviews-page__card-sub">{subtitle}</p> : null}
      </span>
      {!staticCard ? (
        <span className="reviews-page__card-arrow" aria-hidden="true">
          →
        </span>
      ) : null}
    </>
  );

  if (staticCard) {
    return <div className="reviews-page__card reviews-page__card--static">{body}</div>;
  }

  return (
    <a
      className="reviews-page__card"
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {body}
    </a>
  );
}

const ReviewsPage = () => {
  const navigate = useNavigate();
  const spaNavigate = (path) => (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    navigate(path);
    try {
      window.scrollTo(0, 0);
    } catch {
      /* ignore */
    }
  };
  const goDiretor = spaNavigate('/diretor');

  // Selo "Verificada pelo Reclame Aqui" — mesma injeção usada na Home. O
  // <script> é anexado dentro do próprio alvo, então desmonta junto com a
  // página e pode ser reinjetado ao navegar de volta.
  useEffect(() => {
    const target = document.getElementById('ra-verified-seal');
    if (!target) return undefined;
    if (document.getElementById('ra-embed-verified-seal')) return undefined;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'ra-embed-verified-seal';
    script.src = 'https://s3.amazonaws.com/raichu-beta/ra-verified/bundle.js';
    script.async = true;
    script.setAttribute(
      'data-id',
      'cTkxVkRBSko3Smg2S3Budzplc21hbmhvdHRvLXNlcnZpY29zLW1lZGljb3MtbHRkYQ=='
    );
    script.setAttribute('data-target', 'ra-verified-seal');
    script.setAttribute('data-model', 'horizontal_3');
    target.appendChild(script);

    return undefined;
  }, []);

  return (
  <>
    <PageSeo
      path="/reviews"
      title="Avaliações da Consulta+ — Nota 4,8/5 no Google Reviews"
      description="Avaliações da Consulta+: nota 4,8 de 5 no Google Reviews, 4,9 de 5 no Google Play e 4,7 de 5 na App Store, com mais de 10 mil consultas médicas online realizadas por chat. Médico responsável: Dr. Guilherme Esmanhotto (CRM 23.706/DF)."
      breadcrumbs={[
        { name: 'Início', url: '/' },
        { name: 'Avaliações', url: '/reviews' },
      ]}
      jsonLd={REVIEWS_JSONLD}
    />
    <style>{CARD_CSS}</style>
    <LegalPageShell
      eyebrow="Avaliações · Confiança"
      title="Avaliação de nossos clientes"
      subtitle="Mais de 10 mil consultas realizadas · Nota 4,8 ⭐ de 5 no Google Reviews"
      contactEmail="atendimento@consultamais.app"
      contactPrefix="Sobrou alguma dúvida? Escreva para"
      heroImage="/img/heros/hero-reviews-16x9.webp"
      heroAlt="Pintura modernista de pacientes celebrando sob cinco estrelas douradas com um coração ao centro"
    >
      <span className="reviews-page__highlight" role="status">
        ⭐ Nota 4,8 de 5 no Google Reviews
      </span>

      <div className="reviews-page__cards">
        <ReviewCard
          href={APPLE_STORE_URL}
          icon="🍎"
          title="Apple Store"
          subtitle="Nota 4,7 de 5 · Leia aqui todas as avaliações"
        />
        <ReviewCard
          href={PLAY_STORE_URL}
          icon="▶"
          title="Google Play (Android)"
          subtitle="Nota 4,9 de 5 · Leia aqui todas as avaliações"
        />
        <ReviewCard
          staticCard
          icon="G"
          title="Google Reviews"
          subtitle="Nota 4,8 de 5"
        />
      </div>

      <h2>Reputação no Reclame Aqui</h2>
      <p>
        A Consulta+ é <strong>verificada pelo Reclame Aqui</strong>, com reputação{' '}
        <strong>ÓTIMO</strong> e nota média geral <strong>9.7 de 10</strong>.
      </p>
      <div className="reviews-page__ra-reputation">
        <img
          src={`${process.env.PUBLIC_URL || ''}/reputacao-reclame-aqui.png`}
          alt="Reputação ÓTIMO no Reclame Aqui — nota média geral 9.7 de 10"
          width={457}
          height={346}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div
        id="ra-verified-seal"
        className="reviews-page__ra-seal"
        aria-label="Selo Verificada pelo Reclame Aqui"
      />
      <a
        className="reviews-page__ra-verify-link"
        href={RECLAME_AQUI_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          logUxEvent('reviews_ra_verify_link_click', { target: 'reclame_aqui' });
        }}
      >
        Verifique nossa reputação Reclame Aqui
      </a>

      <div className="reviews-page__cta-wrap">
        <button
          type="button"
          className="reviews-page__cta"
          onClick={() => {
            logUxEvent('reviews_cta_click', { target: 'consulta' });
            navigate('/consulta');
          }}
        >
          Peça sua consulta agora
        </button>
      </div>

      <h2>Nosso médico responsável</h2>
      <div className="reviews-page__doctor">
        <p>
          <a href="/diretor" onClick={goDiretor}>
            <strong>Dr. Guilherme Esmanhotto</strong>
          </a>{' '}
          — CRM <strong>23.706/DF</strong>, Diretor Técnico da Consulta+.
        </p>
        <p>
          <a href={CFM_BUSCA_URL} target="_blank" rel="noopener noreferrer">
            Confira no site do Conselho Federal de Medicina
          </a>
        </p>
      </div>

      <h2>Qual o nome de nossa empresa? Onde está localizada e quais os dados de contato?</h2>
      <ul>
        <li>
          <strong>Nome fantasia:</strong> CONSULTA MAIS TELEMEDICINA
        </li>
        <li>
          <strong>Razão social:</strong> Esmanhotto Serviços Médicos LTDA
        </li>
        <li>
          <strong>CNPJ:</strong> 57.946.158/0001-76
        </li>
        <li>
          <strong>Registro no CRM-DF-PJ:</strong> 10.300
        </li>
        <li>
          <strong>Diretor Técnico:</strong> Guilherme Esmanhotto · Médico: CRM-DF 23.706
        </li>
        <li>
          <strong>Endereço:</strong> Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530
        </li>
        <li>
          <strong>E-mail:</strong>{' '}
          <a href="mailto:atendimento@consultamais.app">atendimento@consultamais.app</a>
        </li>
        <li>
          <strong>Telefone:</strong>{' '}
          <a href="tel:+5541988430762">(41) 98843-0762</a>
        </li>
        <li>
          <strong>Ficha no Reclame Aqui:</strong>{' '}
          <a href={RECLAME_AQUI_URL} target="_blank" rel="noopener noreferrer">
            CONSULTA MAIS TELEMEDICINA
          </a>{' '}
          — a Consulta+ (telemedicina) não tem relação com a empresa homônima
          CONSULTAMAIS do setor de análise de crédito.{' '}
          <a href="/consulta-mais-e-consulta-plus">
            Ver desambiguação
          </a>
          .
        </li>
      </ul>

      <h2>Os atestados e receitas digitais são legítimos? São válidos para todo o Brasil?</h2>
      <p>
        Sim! A <strong>Resolução 2314/2022 do Conselho Federal de Medicina</strong> permite que o
        profissional médico emita receitas e atestados médicos digitais via teleconsulta.
      </p>
      <p>
        Não importa onde esteja o paciente — os atestados digitais emitidos são válidos para todo o
        Brasil. Você <strong>não precisa</strong> estar no mesmo estado que seu médico.
      </p>
      <p>
        Os atestados de afastamento laboral são válidos para qualquer empresa ou órgão público
        brasileiro.
      </p>
      <p>
        <a
          href={EXEMPLO_ATESTADO_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={openDocExternalInApp}
        >
          Confira um exemplo de atestado aqui
        </a>
        {' · '}
        <a
          href={EXEMPLO_RECEITA_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={openDocExternalInApp}
        >
          Confira um exemplo de receita aqui
        </a>
      </p>
      <p>
        Para validar a autenticidade de um documento emitido pela Consulta+, acesse{' '}
        <a href="/autenticar">consultamais.app/autenticar</a>.
      </p>
    </LegalPageShell>
  </>
  );
};

export default ReviewsPage;
